import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals';
import { useOrganizationInfo, useUserRoles } from '@features/user';
import { useModal } from '@store/hooks';
import { ROLES } from '@utils/roleUtils';

import { formatCapabilityName } from '../utils/entitlementFormatters';

/**
 * Shared hook for entitlement upgrade action routing.
 *
 * Encapsulates the 3-way upgrade handler:
 * - Solo practice admin → opens EntitlementUpgradeModal
 * - Group practice admin → navigates to /practice/clinicians
 * - Non-admin → opens "Contact Admin" modal
 *
 * Callers pass `bundleId` from the entitlement they already have,
 * avoiding a duplicate `useEntitlement` subscription.
 */
export function useEntitlementUpgradeAction({
  capabilityName,
  bundleId,
  capabilityDisplayName,
  maxWidth = 'md',
}: {
  capabilityName: string;
  bundleId?: string;
  capabilityDisplayName?: string;
  maxWidth?: string;
}): { handleUpgradeClick: () => void } {
  const navigate = useNavigate();
  const { open } = useModal();
  const roles = useUserRoles();
  const orgInfo = useOrganizationInfo();

  const isPracticeAdmin = roles.includes(ROLES.PRACTICE_ADMINISTRATOR);
  const isGroupOrg = orgInfo?.group ?? false;

  const displayName = capabilityDisplayName ?? formatCapabilityName(capabilityName);

  const handleUpgradeClick = useCallback(() => {
    if (isPracticeAdmin && !isGroupOrg) {
      open(MODAL_TYPES.ENTITLEMENT_UPGRADE, {
        bundleId,
        capabilityName,
        capabilityDisplayName: displayName,
        maxWidth,
      });
    } else if (isPracticeAdmin && isGroupOrg) {
      navigate('/practice/clinicians');
    } else {
      open(MODAL_TYPES.ENTITLEMENT_CONTACT_ADMIN);
    }
  }, [isPracticeAdmin, isGroupOrg, open, navigate, bundleId, capabilityName, displayName, maxWidth]);

  return { handleUpgradeClick };
}
